import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import toast from 'react-hot-toast';

import { fetchAthleteCalendar } from '@util/athleteSession';
import { toErrorMessage } from '@util/toErrorMessage';

import DayActivityModal, {
  entriesForDay,
  isClosure,
  isCompletedOverlay,
  isOverlay,
  isPendingLift,
  isRestDay,
  ymd,
} from './DayActivityModal';
import classes from './Calendar.module.css';

const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/** Priority: missed > pending lift > completed > skipped > rest > overlay/closure-only. */
export function primaryDayMark(dayEntries) {
  const slots = (dayEntries || []).filter(
    (entry) => !isOverlay(entry) && !isClosure(entry),
  );
  const overlays = (dayEntries || []).filter((entry) => isOverlay(entry));
  const closures = (dayEntries || []).filter((entry) => isClosure(entry));
  const hasRing = overlays.length > 0 || closures.length > 0;
  const overlayCompleted =
    overlays.length > 0 && overlays.every((entry) => isCompletedOverlay(entry));

  if (slots.some((slot) => (slot.status_formatted || '') === 'missed')) {
    return { mark: 'missed', overlay: hasRing };
  }
  if (slots.some((slot) => isPendingLift(slot))) {
    return { mark: 'pending', overlay: hasRing };
  }
  if (slots.some((slot) => (slot.status_formatted || '') === 'completed')) {
    return {
      mark: 'completed',
      overlay: hasRing,
    };
  }
  if (overlayCompleted && slots.length === 0 && closures.length === 0) {
    return { mark: 'completed', overlay: false };
  }
  if (slots.some((slot) => (slot.status_formatted || '') === 'skipped')) {
    return { mark: 'skipped', overlay: hasRing };
  }
  if (slots.some((slot) => isRestDay(slot))) {
    return { mark: 'rest', overlay: hasRing };
  }
  if (hasRing) {
    if (overlayCompleted && closures.length === 0) {
      return { mark: 'completed', overlay: false };
    }
    return { mark: 'overlay', overlay: true };
  }
  return { mark: null, overlay: false };
}

function buildMonthCells(year, month, selectedDay, todayKey, marksByDay) {
  const first = new Date(year, month, 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];

  for (let i = 0; i < startPad; i += 1) {
    cells.push({ key: `pad-${i}`, muted: true });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = new Date(year, month, day);
    const key = ymd(date);
    const dayMark = marksByDay.get(day) || { mark: null, overlay: false };
    cells.push({
      key,
      day,
      muted: false,
      today: key === todayKey,
      mark: dayMark.mark,
      overlay: dayMark.overlay,
      selected: day === selectedDay,
    });
  }

  return cells;
}

function markClass(mark) {
  switch (mark) {
    case 'missed':
      return classes.calDayMissed;
    case 'pending':
      return classes.calDayPending;
    case 'completed':
      return classes.calDayCompleted;
    case 'skipped':
      return classes.calDaySkipped;
    case 'rest':
      return classes.calDayRest;
    case 'overlay':
      return classes.calDayOverlay;
    default:
      return '';
  }
}

export default function AthleteCalendar() {
  const navigate = useNavigate();
  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);
  const todayKey = ymd(today);

  const [cursor, setCursor] = useState(
    () => new Date(today.getFullYear(), today.getMonth(), 1),
  );
  const [modalDay, setModalDay] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const monthLabel = cursor
    .toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    .toUpperCase();
  const viewingCurrentMonth =
    year === today.getFullYear() && month === today.getMonth();

  useEffect(() => {
    let cancelled = false;
    const fromDate = ymd(new Date(year, month, 1));
    const toDate = ymd(new Date(year, month + 1, 0));
    setLoading(true);
    fetchAthleteCalendar({ fromDate, toDate })
      .then((payload) => {
        if (!cancelled) setEntries(payload.data || []);
      })
      .catch((error) => {
        if (!cancelled) {
          setEntries([]);
          toast.error(toErrorMessage(error));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [year, month]);

  const marksByDay = useMemo(() => {
    const byDay = new Map();
    for (let day = 1; day <= 31; day += 1) {
      const key = ymd(new Date(year, month, day));
      if (new Date(year, month, day).getMonth() !== month) break;
      byDay.set(day, primaryDayMark(entriesForDay(entries, key)));
    }
    return byDay;
  }, [entries, year, month]);

  const cells = useMemo(
    () => buildMonthCells(year, month, modalDay, todayKey, marksByDay),
    [year, month, modalDay, todayKey, marksByDay],
  );

  const modalDate = useMemo(() => {
    if (!modalDay) return null;
    return new Date(year, month, modalDay);
  }, [year, month, modalDay]);

  const modalDateKey = modalDate ? ymd(modalDate) : null;
  const isModalToday = modalDateKey === todayKey;

  const dayEntries = useMemo(() => {
    if (!modalDateKey) return [];
    return entriesForDay(entries, modalDateKey);
  }, [entries, modalDateKey]);

  const modalDateLabel = modalDate
    ? modalDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
      })
    : '';

  function shiftMonth(delta) {
    const next = new Date(year, month + delta, 1);
    setCursor(next);
    setModalDay(null);
  }

  function goToToday() {
    setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
    setModalDay(today.getDate());
  }

  function openDay(day) {
    setModalDay(day);
  }

  function closeDayModal() {
    setModalDay(null);
  }

  return (
    <div className={classes.page}>
      <header className={classes.topBar}>
        <div>
          <p className={classes.eyebrow}>Schedule</p>
          <h1 className={classes.title}>Calendar</h1>
          <p className={classes.subtitle}>
            Lift days, rest, extra sessions, and gym closures — jump into
            today&apos;s workout when it&apos;s on.
          </p>
        </div>
      </header>

      <section className={classes.card}>
        <div className={classes.monthHeader}>
          <h2 className={classes.monthLabel}>{monthLabel}</h2>
          <div className={classes.monthNav}>
            <button
              type="button"
              className={classes.todayBtn}
              onClick={goToToday}
              disabled={viewingCurrentMonth && modalDay === today.getDate()}
            >
              Today
            </button>
            <button
              type="button"
              className={classes.navBtn}
              aria-label="Previous month"
              onClick={() => shiftMonth(-1)}
            >
              <IconChevronLeft size={20} stroke={2} />
            </button>
            <button
              type="button"
              className={classes.navBtn}
              aria-label="Next month"
              onClick={() => shiftMonth(1)}
            >
              <IconChevronRight size={20} stroke={2} />
            </button>
          </div>
        </div>

        <p className={classes.legend}>
          Mustard = pending · Olive = done · Brick tint = missed · Soft =
          rest/skipped · Ring = class/overlay or gym closed · Tap a day for
          details
        </p>

        {loading ? (
          <p className={classes.empty}>Loading schedule…</p>
        ) : (
          <div className={classes.calendarGrid}>
            {DOW.map((label, index) => (
              <div key={`${label}-${index}`} className={classes.calDow}>
                {label}
              </div>
            ))}
            {cells.map((cell) =>
              cell.muted ? (
                <div
                  key={cell.key}
                  className={`${classes.calDay} ${classes.calDayMuted}`}
                />
              ) : (
                <button
                  key={cell.key}
                  type="button"
                  className={[
                    classes.calDay,
                    markClass(cell.mark),
                    cell.overlay && cell.mark !== 'overlay'
                      ? classes.calDayHasOverlay
                      : '',
                    cell.today ? classes.calDayToday : '',
                    cell.selected ? classes.calDaySelected : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => openDay(cell.day)}
                >
                  <span className={classes.calDayNum}>{cell.day}</span>
                  {cell.overlay ? (
                    <span className={classes.calOverlayDot} aria-hidden />
                  ) : null}
                </button>
              ),
            )}
          </div>
        )}
      </section>

      <DayActivityModal
        opened={Boolean(modalDay)}
        onClose={closeDayModal}
        dateLabel={modalDateLabel}
        entries={dayEntries}
        loading={loading}
        isToday={isModalToday}
        onLoadWorkout={(target) => {
          closeDayModal();
          navigate('/workout', {
            state: {
              source: target.source,
              id: target.id,
              onDate: target.onDate,
            },
          });
        }}
      />
    </div>
  );
}
